/**
 * Which Chinese script does this i18next language mean?
 *
 * `i18n.language.startsWith('zh')` is true for every Chinese variant, so code
 * written that way silently serves the Traditional text to a 简体中文 reader.
 * That is not a stylistic shortcut — Traditional and Simplified differ in
 * character, not just in preference, and Hong Kong and mainland readers do not
 * read each other's script interchangeably.
 *
 * Only `zh-Hans` (and the `zh-CN` / `zh-SG` variants that route to it in
 * src/cms/nav.ts's `toNavLocale`) select Simplified.
 */
export type ChineseScript = 'hans' | 'hant' | 'other'

export function chineseScript(language: string | undefined): ChineseScript {
  const lang = (language ?? '').toLowerCase()
  if (lang === 'zh-hans' || lang === 'zh-cn' || lang === 'zh-sg' || lang === 'zh-my') return 'hans'
  if (lang === 'zh-hant' || lang === 'zh-hk' || lang === 'zh-tw' || lang === 'zh-mo') return 'hant'
  // A bare 'zh' is what i18next reports when detection is ambiguous. The site's
  // primary audience is Hong Kong, so Traditional is the safe default — it is
  // also what it did before this helper existed.
  if (lang === 'zh' || lang.startsWith('zh-')) return 'hant'
  return 'other'
}

/**
 * Choose between a Simplified, Traditional and English variant of one string.
 *
 * Every argument is optional: a lecture written before lectures became
 * trilingual has no `hans` variant at all, and the same is true of any content
 * type that has not been translated into simplified yet. Falling back — rather
 * than rendering an empty string — is the point: a visitor who gets the
 * content in a neighbouring script or language is far better served than one
 * who gets a blank field.
 *
 * The chains are deliberately asymmetric:
 *
 *   zh-Hans reader → simplified → **traditional** → english
 *   zh-Hant reader → traditional → **english** → simplified
 *
 * A Traditional reader is never handed Simplified text. Simplified readers
 * routinely read Traditional (most are literate in both), but not the reverse,
 * and a Traditional reader seeing Simplified would reasonably conclude the site
 * has been localised badly. So the last hop is skipped for them, and the field
 * renders empty if there is genuinely nothing but Simplified to show.
 */
export function pickLocalized(
  language: string | undefined,
  hans: string | undefined | null,
  hant: string | undefined | null,
  en: string | undefined | null,
): string {
  switch (chineseScript(language)) {
    case 'hans':
      return hans?.trim() || hant?.trim() || en?.trim() || ''
    case 'hant':
      return hant?.trim() || en?.trim() || ''
    default:
      return en?.trim() || hant?.trim() || hans?.trim() || ''
  }
}
